<template>
  <div class="container-fluid py-4">
    <div class="row">
      <div class="col-12">
        <div class="card mb-4 shadow-sm border-0 bg-light">
          <!-- Header -->
          <div class="card-header pb-4 d-flex justify-content-between align-items-center bg-white border-radius-xl m-3 shadow-sm">
            <div>
              <h6 class="font-weight-bolder mb-0">Services & Prefix Management</h6>
              <p class="text-xs text-secondary mb-0">Click a tile to edit or manage services</p>
            </div>
            <button 
              v-if="$hasPermission('Services & Prefix', 'create') || $hasPermission('Services', 'create')"
              class="btn mb-0 text-white" 
              :style="{ backgroundColor: activeTheme, borderColor: activeTheme }"
              @click="openModal()"
            >
              <i class="fas fa-plus me-2"></i>Add New Service
            </button>
          </div>

          <div class="card-body px-4">
            <div class="row">
              <div 
                v-for="item in services" 
                :key="item.id" 
                class="col-xl-3 col-md-4 col-sm-6 mb-4"
              >
                <!-- Tile -->
                <div class="service-card" :style="getTileStyle(item)" :class="{ 'inactive-tile': !item.status }">
                  
                  <div class="card-actions">
                    <button v-if="$hasPermission('Services & Prefix', 'update') || $hasPermission('Services', 'update')" class="btn btn-icon-only btn-rounded btn-outline-white mb-0 me-2" @click.stop="openModal(item)">
                      <i class="fas fa-pencil-alt"></i>
                    </button>
                    <button v-if="$hasPermission('Services & Prefix', 'delete') || $hasPermission('Services', 'delete')" class="btn btn-icon-only btn-rounded btn-outline-white mb-0" @click.stop="deleteService(item.id)">
                      <i class="far fa-trash-alt"></i>
                    </button>
                  </div>

                  <!-- Centered Body Content -->
                  <div class="tile-body d-flex flex-column align-items-center justify-content-center text-center py-4" @click="openModal(item)">
                    
                    <!-- Icon Container with Perfect Centering -->
                    <div class="icon-box-centered mb-3 d-flex align-items-center justify-content-center shadow">
                      <i :class="getServiceIcon(item.name)" class="text-white icon-size"></i>
                    </div>

                    <h5 class="text-white font-weight-bolder mb-0">{{ item.name }}</h5>
                    <span class="badge badge-sm bg-white text-dark mt-2 px-3">{{ item.prefix }}</span>
                    
                    <div class="mt-3">
                      <span v-if="item.status" class="status-dot bg-success"></span>
                      <span v-else class="status-dot bg-secondary"></span>
                      <small class="text-white text-xs ms-1">{{ item.status ? 'Active' : 'Inactive' }}</small>
                  </div>
                </div>
              </div>
              </div>

              <div v-if="services.length === 0" class="col-12 text-center py-5">
                <div class="text-secondary">
                   <i class="fas fa-folder-open mb-3 fa-3x opacity-4"></i>
                   <p>No services found.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

export default {
  name: "Services",
  data() {
    return {
      services: [],
      userRole: "",
      apiBaseUrl: process.env.VUE_APP_API_BASE_URL,
      activeTheme: localStorage.getItem('themeColor') || '#1e3a5f'
    };
  },
  computed: {
    isSuperAdmin() {
      const role = (localStorage.getItem('userRole') || localStorage.getItem('role') || '').toLowerCase().replace(/\s+/g, '');
      const userStr = localStorage.getItem('user') || localStorage.getItem('user_info') || '{}';
      let user = {};
      try { user = JSON.parse(userStr); } catch (e) { user = {}; }
      return role.includes('superadmin') || user.type === 1 || user.role === 'SuperAdmin' || user.role_name === 'SuperAdmin';
    },
    isDoctor() {
      if (!this.userRole) return false;
      const cleaned = String(this.userRole).toLowerCase().replace(/[^a-z0-9]/gi, '');
      return cleaned === 'doctor' || cleaned.includes('doctor');
    },
    canManageServices() {
      if (this.$hasPermission && typeof this.$hasPermission === 'function') {
        return (
          this.$hasPermission('Services & Prefix', 'create') ||
          this.$hasPermission('Services & Prefix', 'update') ||
          this.$hasPermission('Services', 'create') ||
          this.$hasPermission('Services', 'update')
        );
      }
      if (!this.userRole) return true;
      const role = String(this.userRole).toLowerCase().replace(/['"]+/g, '').replace(/\s+/g, '');
      const allowed = ['superadmin', 'admin', 'tokenmanager', 'devicemanager', 'mediauser', 'doctor'];
      return allowed.includes(role);
    }
  },
  methods: {
    detectRole() {
      let role = localStorage.getItem("userRole") || localStorage.getItem("user_role") || localStorage.getItem("role");
      if (!role) {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            role = user.role_name || (user.role && typeof user.role === 'object' ? user.role.name : user.role);
          } catch (e) { console.error("Error parsing user"); }
        }
      }
      this.userRole = (role || "").toString().replace(/['"]+/g, '').trim();
    },
    getTileStyle(item) {
      if (!item.status) return {}; 
      return {
        background: `linear-gradient(135deg, ${this.activeTheme} 0%, ${this.adjustColor(this.activeTheme, 20)} 100%)`
      };
    },
    adjustColor(hex, percent) {
      var num = parseInt(hex.replace("#",""),16),
      amt = Math.round(2.55 * percent),
      R = (num >> 16) + amt,
      G = (num >> 8 & 0x00FF) + amt,
      B = (num & 0x0000FF) + amt;
      return "#" + (0x1000000 + (R<255?R<1?0:R:255)*0x10000 + (G<255?G<1?0:G:255)*0x100 + (B<255?B<1?0:B:255)).toString(16).slice(1);
    },
    getServiceIcon(name) {
      const lower = (name || '').toLowerCase();
      if (lower.includes('doctor') || lower.includes('opd') || lower.includes('consult')) return 'fas fa-user-md';
      if (lower.includes('cardio') || lower.includes('heart')) return 'fas fa-heartbeat';
      if (lower.includes('ortho') || lower.includes('bone')) return 'fas fa-bone';
      if (lower.includes('radio') || lower.includes('x-ray') || lower.includes('mri')) return 'fas fa-x-ray';
      if (lower.includes('patho') || lower.includes('lab') || lower.includes('blood')) return 'fas fa-vial';
      if (lower.includes('pharma') || lower.includes('medicine')) return 'fas fa-pills';
      if (lower.includes('emerg') || lower.includes('trauma')) return 'fas fa-ambulance';
      if (lower.includes('dental') || lower.includes('tooth')) return 'fas fa-tooth';
      if (lower.includes('eye') || lower.includes('ophta')) return 'fas fa-eye';
      if (lower.includes('child') || lower.includes('pedia')) return 'fas fa-baby';
      if (lower.includes('internet')) return 'fas fa-globe';
      if (lower.includes('tv')) return 'fas fa-tv';
      if (lower.includes('power') || lower.includes('elect')) return 'fas fa-bolt';
      if (lower.includes('water')) return 'fas fa-tint';
      if (lower.includes('gas')) return 'fas fa-fire';
      if (lower.includes('phone') || lower.includes('mobile')) return 'fas fa-phone';
      if (lower.includes('account')) return 'fas fa-university';
      if (lower.includes('cash') || lower.includes('withdraw')) return 'fas fa-money-bill-wave';
      if (lower.includes('customer') || lower.includes('service')) return 'fas fa-concierge-bell';
      return 'fas fa-th-large';
    },
    async fetchServices() {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${this.apiBaseUrl}/v1/admin/services/list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data && res.data.success) {
          this.services = res.data.data;
        }
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    },
    async openModal(item = null) {
      if (item && typeof item === 'object' && !item.id) {
        item = null;
      }
      
      const isEdit = !!(item && item.id);

      if (isEdit) {
        if (this.$hasPermission && typeof this.$hasPermission === 'function') {
          if (!this.$hasPermission('Services & Prefix', 'update') && !this.$hasPermission('Services', 'update')) {
            Swal.fire('Permission Denied', 'You do not have permission to edit services.', 'warning');
            return;
          }
        }
      } else {
        if (this.$hasPermission && typeof this.$hasPermission === 'function') {
          if (!this.$hasPermission('Services & Prefix', 'create') && !this.$hasPermission('Services', 'create')) {
            Swal.fire('Permission Denied', 'You do not have permission to add services.', 'warning');
            return;
          }
        }
      }

      const isDoctor = this.isDoctor;
      const modalTitle = isEdit 
        ? (isDoctor ? 'Edit Medical Service' : 'Edit Service') 
        : (isDoctor ? 'Add New Medical Service' : 'Add New Service');
      const nameLabel = isDoctor ? 'OPD Department / Service Name' : 'Service Name';
      const namePlaceholder = isDoctor ? 'e.g. Cardiology OPD' : 'e.g. Account Opening';
      const prefixPlaceholder = isDoctor ? 'e.g. CARD' : 'e.g. AC';

      const { value: formValues } = await Swal.fire({
        title: modalTitle,
        html:
          `<div class="text-start"><label class="form-label text-xs font-weight-bold">${nameLabel} <span class="text-danger">*</span></label>` +
          `<input id="swal-name" class="swal2-input m-0 w-100" placeholder="${namePlaceholder}" value="${isEdit ? (item.name || '') : ''}"></div>` +
          `<div class="text-start mt-3"><label class="form-label text-xs font-weight-bold">Prefix Code <span class="text-danger">*</span></label>` +
          `<input id="swal-prefix" class="swal2-input m-0 w-100" placeholder="${prefixPlaceholder}" value="${isEdit ? (item.prefix || '') : ''}"></div>` +
          `<div class="text-start mt-3"><label class="form-label text-xs font-weight-bold">Status</label>` +
          `<select id="swal-status" class="swal2-input m-0 w-100">
            <option value="1" ${!isEdit || item.status === 1 || item.status === true || item.status === '1' ? 'selected' : ''}>Active</option>
            <option value="0" ${isEdit && (item.status === 0 || item.status === false || item.status === '0') ? 'selected' : ''}>Inactive</option>
          </select></div>`,
        showCancelButton: true,
        confirmButtonColor: this.activeTheme,
        preConfirm: () => {
          const nameInput = document.getElementById('swal-name');
          const prefixInput = document.getElementById('swal-prefix');
          const name = nameInput ? nameInput.value.trim() : '';
          const prefix = prefixInput ? prefixInput.value.trim() : '';
          if (!name || !prefix) { Swal.showValidationMessage('Required fields missing'); return false; }
          return { name, prefix, status: parseInt(document.getElementById('swal-status').value) };
        }
      });

      if (formValues) {
        try {
          const token = localStorage.getItem('token');
          if (isEdit) {
            await axios.put(`${this.apiBaseUrl}/v1/admin/services/update/${item.id}`, formValues, { headers: { Authorization: `Bearer ${token}` } });
          } else {
            await axios.post(`${this.apiBaseUrl}/v1/admin/services/add`, formValues, { headers: { Authorization: `Bearer ${token}` } });
          }
          Swal.fire({
            icon: 'success',
            title: 'Success',
            text: isEdit ? 'Service updated successfully!' : 'Service added successfully!',
            timer: 2000,
            showConfirmButton: false
          });
          this.fetchServices();
        } catch (error) {
          console.error("Error saving service:", error);
          Swal.fire('Error', error.response?.data?.message || 'Failed to save service', 'error');
        }
      }
    },
    async deleteService(id) {
      if (this.$hasPermission && typeof this.$hasPermission === 'function') {
        if (!this.$hasPermission('Services & Prefix', 'delete') && !this.$hasPermission('Services', 'delete')) {
          Swal.fire('Permission Denied', 'You do not have permission to delete services.', 'warning');
          return;
        }
      }
      const result = await Swal.fire({ title: 'Are you sure?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', confirmButtonText: 'Yes, delete it!' });
      if (result.isConfirmed) {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${this.apiBaseUrl}/v1/admin/services/delete/${id}`, { headers: { Authorization: `Bearer ${token}` } });
          Swal.fire('Deleted!', 'Service deleted successfully.', 'success');
          this.fetchServices();
        } catch (error) { Swal.fire('Error', error.response?.data?.message || 'Failed to delete service', 'error'); }
      }
    }
  },
  mounted() {
    this.detectRole();
    this.fetchServices();
  }
};
</script>

<style scoped>
/* Main Tile Card */
.service-card {
  border-radius: 18px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.service-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2) !important;
}

/* Perfect Center Icon Box */
.icon-box-centered {
  background-color: rgba(255, 255, 255, 0.15);
  width: 55px;
  height: 55px;
  border-radius: 12px;
  transition: background 0.3s ease;
}

.icon-size {
  font-size: 1.4rem !important;
  opacity: 1 !important; /* Made icon fully visible */
}

.tile-body {
  width: 100%;
}

.inactive-tile {
  background: #5e6c7b !important;
  opacity: 0.7;
}

.card-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.service-card:hover .card-actions {
  opacity: 1;
}

.status-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  border: 1px solid white;
}

.btn-outline-white {
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: white;
}
</style>